import { createClient } from '@supabase/supabase-js'
import {
  isExistingAccountSignUpResponse,
  isGenuineNewUnconfirmedSignUp,
  EXISTING_ACCOUNT_SIGNUP_MESSAGE,
  RESERVED_ADMIN_EMAIL_MESSAGE
} from '../utils/authSignUpGuards'

/** Project root only — never https://xxx.supabase.co/rest/v1/ */
export function normalizeSupabaseProjectUrl(raw) {
  const trimmed = String(raw || '').trim()
  if (!trimmed) return ''

  try {
    const url = new URL(trimmed)
    if (url.pathname.startsWith('/rest/v1')) {
      url.pathname = ''
    }
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '')
  } catch {
    return trimmed.replace(/\/rest\/v1\/?.*$/i, '').replace(/\/+$/, '')
  }
}

const supabaseUrl = normalizeSupabaseProjectUrl(process.env.REACT_APP_SUPABASE_URL)
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (typeof window !== 'undefined' && supabaseUrl && /\/rest\/v1/i.test(process.env.REACT_APP_SUPABASE_URL || '')) {
  console.error(
    '[TrophyTime] REACT_APP_SUPABASE_URL included /rest/v1/ — normalized to',
    supabaseUrl,
    '(rebuild with the root URL only)'
  )
}

// Get the production URL for email redirects (host-agnostic: works on
// Cloudflare Pages, Vercel, or any domain the app is served from)
const getEmailRedirectUrl = () => {
  // Prefer the live host when the user is on Pages / a real domain.
  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = window.location.hostname
    if (host && host !== 'localhost' && host !== '127.0.0.1') {
      return window.location.origin
    }
  }

  // Explicit production override (baked at build time)
  if (process.env.REACT_APP_PRODUCTION_URL) {
    return process.env.REACT_APP_PRODUCTION_URL
  }

  // Development fallback
  return process.env.REACT_APP_REDIRECT_URL || 'http://localhost:3000'
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Database table names
export const TABLES = {
  FAMILIES: 'families',
  KIDS: 'kids', 
  TASKS: 'tasks',
  COMPLETIONS: 'task_completions',
  SHOPPING_ITEMS: 'shopping_items',
  ACHIEVEMENTS: 'achievements',
  MESSAGES: 'messages',
  FAMILY_CHALLENGES: 'family_challenges',
  FAMILY_PRESENCE: 'family_presence',
  CALL_SETTINGS: 'call_settings',
  FAMILY_CALLS: 'family_calls',
  CALL_SIGNALING: 'call_signaling',
  CALL_REQUESTS: 'call_requests'
}

// Helper functions for common operations
export const supabaseHelpers = {
  // Auth helpers
  async signUp(email, password, parentName, familyName, captchaToken = null) {
    const options = {
      emailRedirectTo: getEmailRedirectUrl(),
      data: {
        parent_name: parentName,
        family_name: familyName
      }
    }
    if (captchaToken) options.captchaToken = captchaToken

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options
    })

    if (isExistingAccountSignUpResponse(authData, authError)) {
      throw new Error(EXISTING_ACCOUNT_SIGNUP_MESSAGE)
    }

    if (authError) {
      if (authError.message.includes('already registered')) {
        throw new Error(EXISTING_ACCOUNT_SIGNUP_MESSAGE)
      }
      if (authError.message.includes('invalid email')) {
        throw new Error('Please enter a valid email address.');
      }
      if (authError.message.includes('password')) {
        throw new Error('Password must be at least 6 characters long.');
      }
      throw authError;
    }

    if (!authData?.user) {
      throw new Error('Sign up failed. Please try again.')
    }

    // CRITICAL: Only treat as "check your email" for genuine new accounts
    if (!authData.user.email_confirmed_at) {
      if (!isGenuineNewUnconfirmedSignUp(authData)) {
        throw new Error(EXISTING_ACCOUNT_SIGNUP_MESSAGE)
      }
      console.log('📧 Email confirmation required for new user account');
      return { 
        user: authData.user, 
        needsConfirmation: true,
        message: 'Account created! Please check your email and click the confirmation link to complete your registration.'
      };
    }

    console.log('✅ Email already confirmed, checking before family creation');

    // Never turn a platform admin Auth user into a family owner via signup
    try {
      const { data: profileRows } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', authData.user.id)
        .limit(1)
      const role = profileRows?.[0]?.role
      if (role === 'admin' || role === 'super_admin') {
        throw new Error(RESERVED_ADMIN_EMAIL_MESSAGE)
      }
    } catch (profileErr) {
      if (profileErr.message === RESERVED_ADMIN_EMAIL_MESSAGE) throw profileErr
      console.warn('signUp: profile role check skipped:', profileErr.message)
    }

    // If a family already exists for this Auth id or parent email, do not create another
    const normalizedEmail = String(email || '').trim().toLowerCase()
    const { data: existingById } = await supabase
      .from(TABLES.FAMILIES)
      .select('id')
      .eq('id', authData.user.id)
      .limit(1)
    if (existingById?.length) {
      throw new Error(EXISTING_ACCOUNT_SIGNUP_MESSAGE)
    }
    const { data: existingByEmail } = await supabase
      .from(TABLES.FAMILIES)
      .select('id')
      .ilike('parent_email', normalizedEmail)
      .limit(1)
    if (existingByEmail?.length) {
      throw new Error(EXISTING_ACCOUNT_SIGNUP_MESSAGE)
    }

    // Create family record (only after email confirmation)
    const { data: family, error: familyError } = await supabase
      .from(TABLES.FAMILIES)
      .insert([
        {
          id: authData.user.id,
          family_name: familyName,
          parent_name: parentName,
          parent_email: normalizedEmail,
          subscription_tier: 'free',
          subscription_status: 'active',
          settings: {
            currency: "USD",
            language: "en",
            timezone: "UTC",
            daily_points_limit: 1000,
            max_kids_with_login: 5,
            notifications_enabled: true,
            task_approval_required: false,
            require_parent_approval: true,
            islamic_features_enabled: false,
            kid_registration_enabled: true
          },
          islamic_settings: {
            enabled: false,
            location: { lat: null, lng: null, city: null },
            adhan_sound: true,
            friday_reminder: true,
            calculation_method: "MWL",
            prayer_notifications: true
          }
        }
      ])
      .select()
      .single()

    if (familyError) {
      // Handle specific database errors with user-friendly messages
      if (familyError.message.includes('duplicate key value violates unique constraint') && 
          familyError.message.includes('families_parent_email_key')) {
        throw new Error('This email address has already been used to create a family account. Please try logging in instead, or use a different email address.');
      }
      if (familyError.message.includes('violates check constraint')) {
        throw new Error('Please check that all your information is valid and try again.');
      }
      // For other database errors, provide a generic but helpful message
      throw new Error('There was a problem creating your family account. Please try again or contact support if the problem persists.');
    }

    return { user: authData.user, family }
  },

  async signIn(email, password, captchaToken = null) {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    })

    if (authError) {
      // Handle specific login errors with user-friendly messages
      if (authError.message.includes('Invalid login credentials') || 
          authError.message.includes('invalid login')) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      if (authError.message.includes('Email not confirmed')) {
        throw new Error('Please check your email and click the confirmation link before logging in.');
      }
      if (authError.message.includes('too many requests')) {
        throw new Error('Too many login attempts. Please wait a few minutes and try again.');
      }
      if (authError.message.includes('Invalid path')) {
        // Internal misconfig — keep the user-facing text generic
        throw new Error('Something went wrong. Please refresh the page and try again.')
      }
      if (authError.message.includes('captcha')) {
        throw new Error('Security check failed. Complete the CAPTCHA and try again.')
      }
      throw new Error(authError.message || 'Login failed. Please try again.');
    }

    // Get family data with support for both original parents and invited users
    let family;
    let familyId;
    let memberProfile = null; // Track if this is an invited member
    try {
      // First: Try to find family via family_members table (for invited users)
      const { data: familyMembers, error: memberError } = await supabase
        .from('family_members')
        .select('*') // Get full member profile including name, role, etc.
        .eq('user_id', authData.user.id)

      if (familyMembers && familyMembers.length > 0 && !memberError) {
        // Found via family_members - this is an invited user
        memberProfile = familyMembers[0]
        familyId = memberProfile.family_id
        console.log('✅ Found user as invited family member:', {
          memberId: memberProfile.id,
          name: `${memberProfile.first_name} ${memberProfile.last_name}`,
          role: memberProfile.role,
          familyId: familyId
        })
      } else {
        // Second: Try original parent lookup (family.id = user.id)
        const { data: families, error: familyError } = await supabase
          .from(TABLES.FAMILIES)
          .select('*')
          .eq('id', authData.user.id)

        if (families && families.length > 0) {
          family = families[0]
          familyId = family.id
          console.log('✅ Found user as original family creator')
        } else {
          // Third: Try email fallback (for legacy accounts)
          const { data: familiesByEmail } = await supabase
            .from(TABLES.FAMILIES)
            .select('*')
            .eq('parent_email', authData.user.email)

          if (familiesByEmail && familiesByEmail.length > 0) {
            family = familiesByEmail[0]
            familyId = family.id
            console.log('✅ Found user via email fallback')
          } else {
            // Last attempt: Check if user has signup metadata and auto-create family
            console.log('🔍 No family found, checking user metadata for auto-creation...')
            const userMetadata = authData.user.user_metadata
            const parentName = userMetadata?.parent_name
            const familyName = userMetadata?.family_name
            
            if (parentName && familyName && authData.user.email_confirmed_at) {
              console.log('✅ Found signup metadata, auto-creating family record...', {
                parentName,
                familyName,
                userId: authData.user.id,
                email: authData.user.email
              })
              
              try {
                // Use existing helper function to maintain architecture consistency
                const setupResult = await this.completeFamilyAccountSetup(
                  authData.user.id,
                  authData.user.email,
                  parentName,
                  familyName
                )
                
                if (setupResult.success && setupResult.family) {
                  console.log('✅ Family record auto-created successfully during login!')
                  family = setupResult.family
                  familyId = family.id
                } else {
                  console.error('❌ Family setup completed but no family returned:', setupResult)
                  throw new Error('Family auto-creation failed. Please try signing up again or contact support.')
                }
              } catch (createErr) {
                console.error('❌ Auto-create failed during login:', createErr)
                throw new Error('Your account exists but no family record was found. Auto-creation failed. Please try signing up again, or contact support.')
              }
            } else {
              console.error('❌ No family found and no signup metadata available:', {
                hasParentName: !!parentName,
                hasFamilyName: !!familyName,
                emailConfirmed: !!authData.user.email_confirmed_at,
                metadata: userMetadata
              })
              throw new Error('Your family account data was not found. This might be an old account - please try signing up again.')
            }
          }
        }
      }

      // If we found familyId but don't have family object yet, fetch it
      if (familyId && !family) {
        const { data: familyData, error: familyDataError } = await supabase
          .from(TABLES.FAMILIES)
          .select('*')
          .eq('id', familyId)
          .single()

        if (familyDataError) throw familyDataError
        family = familyData
      }

    } catch (error) {
      console.error('❌ Error loading family data:', error)
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        userId: authData.user.id,
        userEmail: authData.user.email
      })
      
      // Provide more specific error message
      if (error.message.includes('Family not found') || error.message.includes('family account data was not found')) {
        // User exists but no family record - offer to create one
        throw new Error('Your account exists but no family record was found. This might happen if you signed up but didn\'t complete the setup. Please try signing up again with the same email, or contact support.')
      }
      
      // Check if it's an RLS policy issue
      if (error.code === '42501' || error.message.includes('permission denied') || error.message.includes('RLS')) {
        throw new Error('Permission denied accessing family data. This might be a database configuration issue. Please contact support.')
      }
      
      // Generic error with more details
      throw new Error(`Unable to load your family data: ${error.message}. Please try again, or contact support if the problem persists.`)
    }

    // Get kids data separately (using correct familyId)
    const { data: kids } = await supabase
      .from(TABLES.KIDS)
      .select('*')
      .eq('family_id', familyId)

    // Get shopping items separately (using correct familyId)
    const { data: shopping_items } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .select('*')
      .eq('family_id', familyId)

    // Attach the related data to family object
    family.kids = kids || []
    family.shopping_items = shopping_items || []
    family.family_challenges = []
    family.messages = []

    // Return formatted user object with proper structure for presence tracking
    // Include memberProfile for invited users so SupabaseApiService can format correctly
    return { 
      user: authData.user, 
      family,
      memberProfile, // Will be null for original parents, populated for invited members
      isInvitedMember: !!memberProfile
    }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Add this new helper function to create missing family records
  async createFamilyRecord(userId, userEmail, parentName, familyName) {
    const { data: family, error: familyError } = await supabase
      .from(TABLES.FAMILIES)
      .insert([
        {
          id: userId,
          family_name: familyName || 'My Family',
          parent_name: parentName || 'Parent',
          parent_email: userEmail,
          created_at: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (familyError) throw familyError;
    return family;
  },

  // Update getFamilyData to handle missing families better
  async getFamilyData(userId) {
    const startTime = Date.now();
    console.log('🔍 [START] Fetching family data for user ID:', userId);
    
    const { data: families, error } = await supabase
      .from(TABLES.FAMILIES)
      .select('*')
      .eq('id', userId);

    console.log(`⏱️ Family query took: ${Date.now() - startTime}ms`);

    if (error) {
      console.error('❌ Error fetching families:', error);
      throw error;
    }

    let family;
    if (!families || families.length === 0) {
      console.log('⚠️ No family found by ID, trying email fallback...');
      // Try to find by email as fallback
      const { data: auth } = await supabase.auth.getUser();
      if (auth.user) {
        const { data: familiesByEmail } = await supabase
          .from(TABLES.FAMILIES)
          .select('*')
          .eq('parent_email', auth.user.email);

        if (familiesByEmail && familiesByEmail.length > 0) {
          console.log('✅ Found family by email fallback');
          family = familiesByEmail[0];
          userId = family.id; // Update userId to match the found family
        } else {
          // Also check family_members table
          console.log('🔍 Checking family_members table for user:', userId);
          const { data: familyMembers, error: memberError } = await supabase
            .from('family_members')
            .select('family_id, user_id')
            .eq('user_id', userId);

          if (memberError) {
            console.error('❌ Error checking family_members:', memberError);
          }

          if (familyMembers && familyMembers.length > 0) {
            const familyMember = familyMembers[0];
            console.log('✅ Found user in family_members, fetching family:', familyMember.family_id);
            const { data: familyData, error: familyDataError } = await supabase
              .from(TABLES.FAMILIES)
              .select('*')
              .eq('id', familyMember.family_id)
              .single();

            if (familyDataError) {
              console.error('❌ Error fetching family by family_id:', familyDataError);
              throw new Error('Family not found. Please log out and log in again.');
            }
            family = familyData;
            userId = family.id;
          } else {
            // Last attempt: Check if user has signup metadata and auto-create family
            // Use the existing completeFamilyAccountSetup function to maintain consistency
            console.log('🔍 Checking user metadata for signup info...');
            
            // Get current user from auth session (auth variable already available from line 319)
            if (auth.user) {
              const userMetadata = auth.user.user_metadata;
              const parentName = userMetadata?.parent_name;
              const familyName = userMetadata?.family_name;
              
              if (parentName && familyName && auth.user.email_confirmed_at) {
                console.log('✅ Found signup metadata, completing family setup...');
                try {
                  // Use existing helper function to maintain architecture consistency
                  const setupResult = await this.completeFamilyAccountSetup(
                    auth.user.id,
                    auth.user.email,
                    parentName,
                    familyName
                  );
                  
                  if (setupResult.success && setupResult.family) {
                    console.log('✅ Family record auto-created successfully!');
                    family = setupResult.family;
                    userId = family.id;
                  } else {
                    throw new Error('Family setup completed but family record not returned.');
                  }
                } catch (createErr) {
                  console.error('❌ Auto-create failed:', createErr);
                  throw new Error('Family not found. Your account exists but no family record was found. Please try signing up again, or contact support.');
                }
              } else {
                console.error('❌ No family found by any method');
                throw new Error('Family not found. Your account exists but no family record was found. This might happen if you signed up but didn\'t complete the setup. Please try signing up again, or contact support.');
              }
            } else {
              console.error('❌ No user session found');
              throw new Error('Family not found. Please log out and log in again.');
            }
          }
        }
      } else {
        throw new Error('Family not found. Please log out and log in again.');
      }
    } else {
      family = families[0];
    }
    
    console.log('📊 Found family:', family);

    // Get kids data with enhanced logging
    console.log('🔍 Fetching kids for family_id:', userId);
    const { data: kids, error: kidsError } = await supabase
      .from(TABLES.KIDS)
      .select('*')
      .eq('family_id', userId);

    if (kidsError) {
      console.error('❌ Error fetching kids:', kidsError);
    } else {
      console.log('👥 Found kids:', kids?.length || 0, 'kids:', kids);
    }

    // Get all tasks for the family's kids
    let allTasks = [];
    let allTaskCompletions = [];
    
    if (kids && kids.length > 0) {
      const taskStartTime = Date.now();
      console.log('🔍 [START] Fetching tasks for all kids...');
      const kidIds = kids.map(kid => kid.id);
      console.log('  Kid IDs to fetch for:', kidIds);
      
      // Fetch tasks and completions in parallel for better performance
      const [tasksResult, completionsResult] = await Promise.all([
        // Fetch all current tasks
        supabase
          .from(TABLES.TASKS)
          .select('*')
          .in('kid_id', kidIds)
          .order('created_at', { ascending: false }),
        
        // Fetch task completions - increased limit to support historical views
        // This allows users to view tasks from weeks/months ago
        supabase
          .from(TABLES.COMPLETIONS)
          .select('*')
          .in('kid_id', kidIds)
          .order('completed_at', { ascending: false })
          .limit(500) // Increased limit to support better historical viewing
      ]);

      console.log(`⏱️ Tasks & Completions queries took: ${Date.now() - taskStartTime}ms`);

      // Handle tasks result
      if (tasksResult.error) {
        console.error('❌ Error fetching tasks:', tasksResult.error);
        console.error('❌ Full error:', JSON.stringify(tasksResult.error, null, 2));
      } else {
        allTasks = tasksResult.data || [];
        console.log('📝 Found tasks:', allTasks.length, 'tasks');
        if (allTasks.length > 0) {
          console.log('  Sample task:', allTasks[0]);
        }
      }

      // Handle completions result - simplified without expensive joins
      if (completionsResult.error) {
        console.error('❌ Error fetching task completions:', completionsResult.error);
        console.error('❌ Full error:', JSON.stringify(completionsResult.error, null, 2));
      } else {
        allTaskCompletions = completionsResult.data || [];
        console.log('📋 Found task completions:', allTaskCompletions.length, 'completions');
        if (allTaskCompletions.length > 0) {
          console.log('  Sample completion:', allTaskCompletions[0]);
          console.log('  Date range:', {
            oldest: allTaskCompletions[allTaskCompletions.length - 1]?.completed_at,
            newest: allTaskCompletions[0]?.completed_at
          });
        }
      }
    } else {
      console.log('⚠️ No kids found, skipping task/completion fetch');
    }

    // Attach tasks and completions to their respective kids
    const kidsWithTasks = kids?.map(kid => {
      const kidTasks = allTasks.filter(task => task.kid_id === kid.id);
      const kidCompletions = allTaskCompletions.filter(completion => completion.kid_id === kid.id);
      
      console.log(`📊 Kid ${kid.name}: ${kidTasks.length} active tasks, ${kidCompletions.length} completed tasks`);
      
      // Map completions to a task-like format for display
      // Find the original task details from allTasks or use completion data
      const completedTasksHistory = kidCompletions.map(completion => {
        const originalTask = allTasks.find(task => task.id === completion.task_id);
        return {
          id: completion.task_id,
          title: originalTask?.title || `Task (${completion.points_awarded} pts)`,
          description: originalTask?.description || 'Completed task',
          points: completion.points_awarded || originalTask?.points || 0,
          category: originalTask?.category || 'other',
          icon: originalTask?.icon || '✅',
          completed: true,
          completed_at: completion.completed_at,
          notes: completion.notes,
          photo_proof: completion.photo_proof,
          verified_by_parent: completion.verified_by_parent,
          isHistorical: true // Mark as historical completion
        };
      });

      return {
        ...kid,
        tasks: kidTasks,
        completedTasksHistory: completedTasksHistory,
        completed_tasks: kidCompletions.length
      };
    }) || [];

    console.log('👥 Kids with tasks attached:');
    kidsWithTasks.forEach(kid => {
      console.log(`  - ${kid.name}: ${kid.tasks?.length || 0} tasks, ${kid.completedTasksHistory?.length || 0} history items`);
    });

    // Get shopping items with logging
    console.log('🔍 Fetching shopping items for family_id:', userId);
    const { data: shopping_items, error: shoppingError } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .select('*')
      .eq('family_id', userId);

    if (shoppingError) {
      console.error('❌ Error fetching shopping items:', shoppingError);
    } else {
      console.log('🛒 Found shopping items:', shopping_items?.length || 0);
    }

    // Attach the related data to family object
    family.kids = kidsWithTasks;
    family.shopping_items = shopping_items || [];
    family.family_challenges = [];
    family.messages = [];

    const totalTime = Date.now() - startTime;
    console.log(`⏱️ [COMPLETE] Total getFamilyData took: ${totalTime}ms`);
    console.log('📊 Final family data summary:');
    console.log(`  - ${kidsWithTasks.length} kids`);
    console.log(`  - ${allTasks.length} total active tasks`);
    console.log(`  - ${allTaskCompletions.length} total completions`);
    kidsWithTasks.forEach(kid => {
      console.log(`  - ${kid.name}: ${kid.tasks?.length || 0} tasks, ${kid.completedTasksHistory?.length || 0} history`);
    });
    
    if (totalTime > 5000) {
      console.warn('⚠️ WARNING: Data loading took over 5 seconds! This may indicate a performance issue.');
    }
    
    return family;
  },

  // Kids management
  async addKid(familyId, kidData) {
    const { data, error } = await supabase
      .from(TABLES.KIDS)
      .insert([
        {
          family_id: familyId,
          name: kidData.name,
          age: kidData.age,
          avatar: kidData.avatar,
          email: kidData.email || null,
          can_login: kidData.canLogin || false,
          level: 1,
          points: 0,
          total_points: 0,
          streak: 0,
          best_streak: 0,
          profile_photo: kidData.profilePhoto || null,
          settings: kidData.settings || {
            theme: "auto",
            daily_goal: 100,
            privacy_mode: false,
            notifications: true,
            sound_effects: true,
            preferred_categories: []
          },
          stats: kidData.stats || {
            days_active: 0,
            weekly_points: 0,
            monthly_points: 0,
            tasks_completed: 0,
            achievements_earned: 0,
            favorite_categories: []
          },
          islamic_profile: kidData.islamicProfile || {
            quran_reading: false,
            prayer_tracking: false,
            islamic_achievements: []
          }
        }
      ])
      .select()
      .single()

    if (error) throw error
    return data
  },

  async updateKid(kidId, updates) {
    const { data, error } = await supabase
      .from(TABLES.KIDS)
      .update(updates)
      .eq('id', kidId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async deleteKid(kidId) {
    const { error } = await supabase
      .from(TABLES.KIDS)
      .delete()
      .eq('id', kidId)

    if (error) throw error
  },

  // Task management
  async addTask(kidId, taskData) {
    // First get the kid to find their family_id
    const { data: kid, error: kidError } = await supabase
      .from(TABLES.KIDS)
      .select('family_id')
      .eq('id', kidId)
      .single();

    if (kidError) {
      console.error('❌ Error fetching kid for family_id:', kidError);
      throw kidError;
    }

    if (!kid || !kid.family_id) {
      throw new Error('Could not find family for this kid');
    }

    console.log('🔍 Adding task for kid:', kidId, 'family:', kid.family_id);

    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .insert([
        {
          kid_id: kidId,
          family_id: kid.family_id,
          title: taskData.title,
          description: taskData.description || null,
          points: taskData.points || 10,
          category: taskData.category || 'other',
          recurring: taskData.recurring || false,
          recurring_pattern: taskData.recurringPattern || null,
          recurring_data: taskData.recurringData || null,
          due_date: taskData.dueDate || null,
          reminder_time: taskData.reminderTime || null,
          requires_verification: taskData.requiresVerification || false,
          priority: taskData.priority || 1,
          estimated_minutes: taskData.estimatedMinutes || null,
          location_required: taskData.locationRequired || null,
          photo_required: taskData.photoRequired || false,
          created_by: taskData.createdBy || null,
          assigned_by: taskData.assignedBy || null
        }
      ])
      .select()
      .single()

    if (error) {
      console.error('❌ Error inserting task:', error);
      throw error;
    }
    
    console.log('✅ Task added successfully:', data);
    return data
  },

  async removeTask(taskId) {
    const { error } = await supabase
      .from(TABLES.TASKS)
      .delete()
      .eq('id', taskId)

    if (error) throw error
    return true
  },

  async updateTask(taskId, updates) {
    const { data, error } = await supabase
      .from(TABLES.TASKS)
      .update(updates)
      .eq('id', taskId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async completeTask(kidId, taskId, completionData = {}) {
    // Use a single transaction for all operations
    const { data, error } = await supabase.rpc('complete_task_transaction', {
      p_kid_id: kidId,
      p_task_id: taskId,
      p_notes: completionData.notes || null,
      p_photo_proof: completionData.photoProof || null
    });

    if (error) throw error;
    return data;
  },

  // Shopping list
  async addShoppingItem(familyId, itemData) {
    console.log('🔄 Adding shopping item:', itemData, 'to family:', familyId);
    
    const { data, error } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .insert([
        {
          family_id: familyId,
          item_name: itemData.name || itemData.item || itemData.title || 'Unnamed Item',
          description: itemData.description || null,
          category: itemData.category || 'other',
          quantity: itemData.quantity || 1,
          estimated_price: itemData.estimatedPrice || null,
          urgent: itemData.urgent || false,
          purchased: false,
          added_by_kid_id: itemData.addedByKidId || null,
          added_by_user_id: itemData.addedByUserId || null,
          notes: itemData.notes || null
        }
      ])
      .select()
      .single()

    if (error) {
      console.error('❌ Shopping item insert error:', error);
      console.error('❌ Full error details:', JSON.stringify(error, null, 2));
      throw error;
    }
    
    console.log('✅ Shopping item added successfully:', data);
    return data
  },

  async toggleShoppingItem(itemId, purchasedBy = null) {
    // First get the current state of the item
    const { data: currentItem, error: fetchError } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .select('purchased')
      .eq('id', itemId)
      .single()

    if (fetchError) throw fetchError

    const isPurchasing = !currentItem.purchased;
    
    // Update with advanced purchase tracking
    const { data, error } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .update({ 
        purchased: isPurchasing,
        purchased_at: isPurchasing ? new Date().toISOString() : null,
        purchased_by: isPurchasing ? purchasedBy : null
      })
      .eq('id', itemId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async deleteShoppingItem(itemId) {
    const { error } = await supabase
      .from(TABLES.SHOPPING_ITEMS)
      .delete()
      .eq('id', itemId)

    if (error) throw error
  },

  // Advanced kid management
  async updateKidStats(kidId, statsUpdate) {
    const { data, error } = await supabase
      .from(TABLES.KIDS)
      .update({
        stats: statsUpdate,
        updated_at: new Date().toISOString()
      })
      .eq('id', kidId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async updateKidSettings(kidId, settingsUpdate) {
    const { data, error } = await supabase
      .from(TABLES.KIDS)
      .update({
        settings: settingsUpdate,
        updated_at: new Date().toISOString()
      })
      .eq('id', kidId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async updateKidIslamicProfile(kidId, islamicProfileUpdate) {
    const { data, error } = await supabase
      .from(TABLES.KIDS)
      .update({
        islamic_profile: islamicProfileUpdate,
        updated_at: new Date().toISOString()
      })
      .eq('id', kidId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Advanced family management
  async updateFamilySettings(familyId, settingsUpdate) {
    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .update({
        settings: settingsUpdate,
        updated_at: new Date().toISOString()
      })
      .eq('id', familyId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  async updateFamilyIslamicSettings(familyId, islamicSettingsUpdate) {
    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .update({
        islamic_settings: islamicSettingsUpdate,
        updated_at: new Date().toISOString()
      })
      .eq('id', familyId)
      .select()
      .single()

    if (error) throw error
    return data
  },

  // Task completion with verification
  async completeTaskAdvanced(kidId, taskId, completionData = {}) {
    const { data: task } = await supabase
      .from(TABLES.TASKS)
      .select('*')
      .eq('id', taskId)
      .single()

    if (!task) throw new Error('Task not found')

    // Update task completion
    const { data: updatedTask, error: taskError } = await supabase
      .from(TABLES.TASKS)
      .update({
        completed: true,
        completed_at: new Date().toISOString(),
        verified_by_parent: completionData.verifiedByParent || !task.requires_verification
      })
      .eq('id', taskId)
      .select()
      .single()

    if (taskError) throw taskError

    // Insert task completion record
    const { data: completion, error: completionError } = await supabase
      .from(TABLES.COMPLETIONS)
      .insert([
        {
          kid_id: kidId,
          task_id: taskId,
          points_awarded: task.points,
          notes: completionData.notes || null,
          photo_proof: completionData.photoProof || null,
          verified_by_parent: completionData.verifiedByParent || !task.requires_verification
        }
      ])
      .select()
      .single()

    if (completionError) throw completionError

    // Update kid points and stats if verified or doesn't require verification
    if (!task.requires_verification || completionData.verifiedByParent) {
      const { data: kid } = await supabase
        .from(TABLES.KIDS)
        .select('points, total_points, stats')
        .eq('id', kidId)
        .single()

      if (kid) {
        const newStats = {
          ...kid.stats,
          tasks_completed: (kid.stats.tasks_completed || 0) + 1,
          weekly_points: (kid.stats.weekly_points || 0) + task.points,
          monthly_points: (kid.stats.monthly_points || 0) + task.points
        }

        await supabase
          .from(TABLES.KIDS)
          .update({
            points: kid.points + task.points,
            total_points: kid.total_points + task.points,
            stats: newStats
          })
          .eq('id', kidId)
      }
    }

    return { task: updatedTask, completion }
  },

  // ========================================================
  // CALLING MANAGEMENT FUNCTIONS
  // ========================================================

  async updateCallingSettings(callingEnabled, callingSettings = {}) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get the user's family_id - support both original parents and invited users
    let familyId;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      familyId = familyMember.family_id;
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: family } = await supabase
        .from(TABLES.FAMILIES)
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      
      if (family) {
        familyId = family.id;
      }
    }
    
    if (!familyId) throw new Error('Family not found');

    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .update({
        calling_enabled: callingEnabled,
        calling_settings: {
          ...callingSettings,
          updated_at: new Date().toISOString()
        }
      })
      .eq('id', familyId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getCallingSettings() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get the user's family_id - support both original parents and invited users
    let familyId;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      familyId = familyMember.family_id;
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: family } = await supabase
        .from(TABLES.FAMILIES)
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      
      if (family) {
        familyId = family.id;
      }
    }
    
    if (!familyId) throw new Error('Family not found');

    const { data, error } = await supabase
      .from(TABLES.FAMILIES)
      .select('calling_enabled, calling_settings, subscription_tier')
      .eq('id', familyId)
      .single();

    if (error) throw error;
    return data;
  },

  async updateKidCallingPermissions(kidId, permissions) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // First verify the kid belongs to this family
    const { data: kid, error: kidError } = await supabase
      .from(TABLES.KIDS)
      .select('family_id')
      .eq('id', kidId)
      .single();

    if (kidError) throw kidError;
    if (kid.family_id !== session.user.id) throw new Error('Unauthorized');

    // Update call settings
    const { data, error } = await supabase
      .from('call_settings')
      .update({
        ...permissions,
        updated_at: new Date().toISOString()
      })
      .eq('kid_id', kidId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async checkFamilyCallingFeatures() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get the user's family_id - support both original parents and invited users
    let familyId;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      familyId = familyMember.family_id;
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: family } = await supabase
        .from(TABLES.FAMILIES)
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      
      if (family) {
        familyId = family.id;
      }
    }
    
    if (!familyId) throw new Error('Family not found');

    const { data, error } = await supabase.rpc('family_has_calling_features', {
      family_uuid: familyId
    });

    if (error) throw error;
    return { hasCallingFeatures: data };
  },

  // ========================================================
  // PRESENCE SYSTEM FUNCTIONS
  // ========================================================

  async updatePresence(userType, userId, userName = '', isOnline = true, isAvailableForCalls = true, statusMessage = '', currentActivity = 'active', deviceInfo = {}) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get the user's family_id - support both original parents and invited users
    let family;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      const { data: familyData } = await supabase
        .from('families')
        .select('id')
        .eq('id', familyMember.family_id)
        .single();
      family = familyData;
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: familyData } = await supabase
        .from('families')
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      family = familyData;
    }
    
    if (!family) throw new Error('Family not found');

    const { data, error } = await supabase.rpc('update_presence_heartbeat', {
      p_family_id: family.id,
      p_user_type: userType,
      p_user_id: userId,
      p_user_name: userName,
      p_is_online: isOnline,
      p_is_available_for_calls: isAvailableForCalls,
      p_status_message: statusMessage,
      p_current_activity: currentActivity,
      p_device_info: deviceInfo
    });

    if (error) throw error;
    return data;
  },

  async markUserOffline(userId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get the user's family_id - support both original parents and invited users
    let family;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      const { data: familyData } = await supabase
        .from('families')
        .select('id')
        .eq('id', familyMember.family_id)
        .single();
      family = familyData;
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: familyData } = await supabase
        .from('families')
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      family = familyData;
    }
    
    if (!family) throw new Error('Family not found');

    const { data, error } = await supabase.rpc('mark_user_offline', {
      p_family_id: family.id,
      p_user_id: userId
    });

    if (error) throw error;
    return data;
  },

  async getFamilyPresence(providedFamilyId = null) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    let familyId = providedFamilyId;
    
    // If no family_id provided, do the lookup (fallback for backward compatibility)
    if (!familyId) {
      // Get the user's family_id - support both original parents and invited users
      let family;
      
      // First: Try to find family via family_members table (for invited users)
      const { data: familyMembers } = await supabase
        .from('family_members')
        .select('family_id')
        .eq('user_id', session.user.id);

      if (familyMembers && familyMembers.length > 0) {
        const familyMember = familyMembers[0];
        // Found via family_members - this is an invited user
        const { data: familyData } = await supabase
          .from('families')
          .select('id')
          .eq('id', familyMember.family_id)
          .single();
        family = familyData;
      } else {
        // Second: Try original parent lookup (families.parent_email = user.email)
        const { data: familyData } = await supabase
          .from('families')
          .select('id')
          .eq('parent_email', session.user.email)
          .single();
        family = familyData;
      }
      
      if (!family) throw new Error('Family not found');
      familyId = family.id;
    }

    console.log('📊 Fetching family presence for familyId:', familyId);

    // Heartbeats arrive every 30s; anything older than 90s is a ghost row from
    // a window that closed/crashed without marking itself offline. Without this
    // filter, people show as "online" forever and calls to them ring into the void.
    const staleCutoff = new Date(Date.now() - 90 * 1000).toISOString();

    const { data, error } = await supabase
      .from(TABLES.FAMILY_PRESENCE)
      .select('*')
      .eq('family_id', familyId)
      .eq('is_online', true)
      .gte('last_heartbeat', staleCutoff)
      .order('last_heartbeat', { ascending: false }); // Fixed: was 'last_seen'

    if (error) {
      console.error('❌ Error fetching family presence:', error);
      throw error;
    }
    
    console.log('✅ Family presence query result:', data?.length || 0, 'members');
    return data || [];
  },

  async subscribeToPresenceChanges(familyId, callback) {
    const subscription = supabase
      .channel(`family_presence_${familyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'family_presence',
          filter: `family_id=eq.${familyId}`
        },
        callback
      )
      .subscribe();

    return subscription;
  },

  async getKidCallSettings(kidId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from(TABLES.CALL_SETTINGS)
      .select('*')
      .eq('kid_id', kidId)
      .eq('family_id', session.user.id)
      .single();

    if (error) throw error;
    return data;
  },

  async getCallSettings(memberId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from(TABLES.CALL_SETTINGS)
      .select('*')
      .eq('member_id', memberId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  },

  async updateCallSettings(memberId, settings) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get family_id for the member
    const { data: member } = await supabase
      .from(TABLES.FAMILY_MEMBERS)
      .select('family_id')
      .eq('id', memberId)
      .single();

    if (!member) throw new Error('Family member not found');

    // Upsert call settings
    const { data, error } = await supabase
      .from(TABLES.CALL_SETTINGS)
      .upsert({
        family_id: member.family_id,
        member_id: memberId,
        ...settings,
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ========================================================
  // CALLING & SIGNALING FUNCTIONS
  // ========================================================

  async initiateCall(callData) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // A window whose UI identity got out of sync with the live session can end
    // up "calling" the very account it is signed in as. Fail loudly instead of
    // creating a ghost call that rings nobody.
    if (callData.targetUserId === session.user.id) {
      throw new Error(
        `This window is signed in as ${session.user.email}, which is the same account you are trying to call. ` +
        'Please reload both windows and log each account in again.'
      );
    }

    // Get family_id using the same logic as presence system
    let familyId;
    
    // First: Try to find family via family_members table (for invited users)
    const { data: familyMembers } = await supabase
      .from('family_members')
      .select('family_id')
      .eq('user_id', session.user.id);

    if (familyMembers && familyMembers.length > 0) {
      const familyMember = familyMembers[0];
      // Found via family_members - this is an invited user
      familyId = familyMember.family_id;
      console.log('📞 Found family ID via family_members (invited user):', familyId);
    } else {
      // Second: Try original parent lookup (families.parent_email = user.email)
      const { data: family } = await supabase
        .from('families')
        .select('id')
        .eq('parent_email', session.user.email)
        .single();
      
      if (family) {
        familyId = family.id;
        console.log('📞 Found family ID via families table (family owner):', familyId);
      }
    }
    
    if (!familyId) throw new Error('Family not found for this user');

    console.log('📞 Initiating call with family_id:', familyId);

    // Use the database RPC function to initiate call
    // CRITICAL: pass the SDP offer so the callee can read it from the call row
    const { data, error } = await supabase.rpc('initiate_call', {
      p_family_id: familyId,
      p_caller_id: session.user.id,
      p_caller_type: 'parent', // Assuming parent is calling
      p_receiver_id: callData.targetUserId,
      p_receiver_type: callData.targetUserType,
      p_call_type: callData.callType,
      p_offer_data: callData.offer || null
    });

    if (error) {
      console.error('📞 RPC initiate_call error:', error);
      throw error;
    }
    
    console.log('📞 Call initiated successfully via RPC:', data);
    
    // RPC functions return arrays, so access the first result
    const result = Array.isArray(data) ? data[0] : data;
    
    // Return the RPC result with the actual call ID generated by the database
    return {
      success: result.success,
      message: result.message,
      callId: result.call_id  // Use the call_id returned by the RPC function
    };
  },

  async answerCall(callId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Update call status to answered
    const { data, error } = await supabase
      .from(TABLES.FAMILY_CALLS)
      .update({
        status: 'answered',
        answered_at: new Date().toISOString()
      })
      .eq('id', callId)
      .eq('receiver_id', session.user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async rejectCall(callId) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Use the RPC function to reject call
    const { data, error } = await supabase.rpc('reject_call', {
      p_call_id: callId
    });

    if (error) throw error;
    return data;
  },

  async endCall(callId, disconnectReason = 'normal') {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Use the RPC function to end call
    const { data, error } = await supabase.rpc('end_call', {
      p_call_id: callId,
      p_disconnect_reason: disconnectReason
    });

    if (error) throw error;
    return data;
  },

  /**
   * Best-effort call teardown that survives page unload (refresh/close/navigate).
   * A normal async request is aborted when the tab unloads, so this uses a
   * `keepalive` fetch straight to the Supabase REST endpoint. Setting the row to
   * `ended` triggers the peer's call-lifecycle subscription → instant clean end
   * (no 45s "reconnecting" limbo). RLS allows a call participant to update the row.
   */
  endCallBeacon(callId, accessToken) {
    if (!callId || !accessToken || !supabaseUrl) return false;
    try {
      const url = `${supabaseUrl}/rest/v1/family_calls?id=eq.${encodeURIComponent(callId)}`;
      fetch(url, {
        method: 'PATCH',
        keepalive: true,
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal'
        },
        body: JSON.stringify({ status: 'ended' })
      }).catch(() => { /* unload path — nothing we can do */ });
      return true;
    } catch (_) {
      return false;
    }
  },

  async getAccessToken() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (_) {
      return null;
    }
  },

  async sendCallSignal(signalData) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Get call details to determine recipient
    const { data: call, error: callError } = await supabase
      .from(TABLES.FAMILY_CALLS)
      .select('caller_id, receiver_id')
      .eq('id', signalData.callId)
      .single();

    if (callError) throw callError;

    // Determine recipient (the other party in the call)
    const toUserId = call.caller_id === session.user.id ? call.receiver_id : call.caller_id;

    // Insert signaling data
    const { data, error } = await supabase
      .from(TABLES.CALL_SIGNALING)
      .insert({
        call_id: signalData.callId,
        from_user_id: session.user.id,
        to_user_id: toUserId,
        signal_type: signalData.type,
        signal_data: signalData.data
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async subscribeToCallSignaling(callId, callback) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const myUserId = session.user.id;

    // Replay signals that arrived before this device subscribed (critical for ICE/answer)
    try {
      const { data: existing } = await supabase
        .from(TABLES.CALL_SIGNALING)
        .select('*')
        .eq('call_id', callId)
        .eq('to_user_id', myUserId)
        .order('created_at', { ascending: true });

      if (existing?.length) {
        console.log(`📨 Replaying ${existing.length} missed signal(s) for call`, callId);
        for (const row of existing) {
          if (row.from_user_id === myUserId) continue;
          callback({ new: row, eventType: 'INSERT' });
        }
      }
    } catch (e) {
      console.warn('⚠️ Could not replay call signals:', e.message);
    }

    const subscription = await new Promise((resolve, reject) => {
      const channel = supabase
        .channel(`call_signaling_${callId}_${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'call_signaling',
            filter: `call_id=eq.${callId}`
          },
          (payload) => {
            const row = payload.new;
            if (!row) return;
            // Never process our own outbound signals
            if (row.from_user_id === myUserId) return;
            // Only deliver signals meant for this user
            if (!row.to_user_id || row.to_user_id === myUserId) {
              callback(payload);
            }
          }
        )
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') resolve(channel);
          else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            reject(err || new Error(`Call signaling channel failed: ${status}`));
          }
        });
    });

    return subscription;
  },

  async getCallHistory(limit = 50) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from(TABLES.FAMILY_CALLS)
      .select('*')
      .eq('family_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async getPendingIncomingCalls() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const myUserId = session.user.id;
    const since = new Date(Date.now() - 90 * 1000).toISOString();

    const { data, error } = await supabase
      .from('family_calls')
      .select('*')
      .eq('receiver_id', myUserId)
      .neq('caller_id', myUserId)
      .in('status', ['initiated', 'ringing'])
      .gte('created_at', since)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async subscribeToIncomingCalls(callback) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const myUserId = session.user.id;

    console.log('📡 Setting up call subscription for user ID:', myUserId);

    // Only ring for calls addressed to THIS login. Previously this matched
    // every parent in the family (including the caller's own window), which
    // made outgoing calls trigger the incoming-call UI and let windows ring
    // for users whose session they no longer held.
    // Wait until the channel is actually SUBSCRIBED — returning early used to
    // leave a window deaf until the next full remount.
    const channelName = `incoming_calls_${myUserId}_${Date.now()}`;

    const subscription = await new Promise((resolve, reject) => {
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'family_calls'
          },
          (payload) => {
            const callData = payload.new;
            // Strict: only the callee rings — never the caller's other devices
            if (
              callData &&
              callData.receiver_id === myUserId &&
              callData.caller_id !== myUserId &&
              (callData.status === 'initiated' || callData.status === 'ringing' || !callData.status)
            ) {
              console.log('📱 Incoming call detected for user:', callData.receiver_id);
              callback(payload);
            }
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Incoming-call channel status:', status, err || '');
          if (status === 'SUBSCRIBED') resolve(channel);
          else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            reject(err || new Error(`Incoming-call channel failed: ${status}`));
          }
        });
    });

    return subscription;
  },

  /**
   * Notify all sessions of this user when a call they are in (caller or
   * receiver) changes status — so a second phone/browser stops ringing.
   */
  async subscribeToCallLifecycle(callback) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const myUserId = session.user.id;
    const channelName = `call_lifecycle_${myUserId}_${Date.now()}`;

    const subscription = await new Promise((resolve, reject) => {
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'family_calls'
          },
          (payload) => {
            const callData = payload.new;
            if (!callData) return;
            if (callData.receiver_id === myUserId || callData.caller_id === myUserId) {
              callback(callData);
            }
          }
        )
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') resolve(channel);
          else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            reject(err || new Error(`Call lifecycle channel failed: ${status}`));
          }
        });
    });

    return subscription;
  },

  // Complete family account setup after email confirmation
  async completeFamilyAccountSetup(userId, email, parentName, familyName) {
    try {
      console.log('🔄 Completing family account setup after email confirmation');

      // Create family record
      const { data: family, error: familyError } = await supabase
        .from(TABLES.FAMILIES)
        .insert([
          {
            id: userId,
            family_name: familyName,
            parent_name: parentName,
            parent_email: email,
            subscription_tier: 'free',
            subscription_status: 'active',
            settings: {
              currency: "USD",
              language: "en",
              timezone: "UTC",
              daily_points_limit: 1000,
              max_kids_with_login: 5,
              notifications_enabled: true,
              task_approval_required: false,
              require_parent_approval: true,
              islamic_features_enabled: false,
              kid_registration_enabled: true
            },
            islamic_settings: {
              enabled: false,
              location: { lat: null, lng: null, city: null },
              adhan_sound: true,
              friday_reminder: true,
              calculation_method: "MWL",
              prayer_notifications: true
            }
          }
        ])
        .select()
        .single();

      if (familyError) {
        // Handle specific database errors with user-friendly messages
        if (familyError.message.includes('duplicate key value violates unique constraint') && 
            familyError.message.includes('families_parent_email_key')) {
          throw new Error('This email address has already been used to create a family account. Please try logging in instead, or use a different email address.');
        }
        if (familyError.message.includes('violates check constraint')) {
          throw new Error('Please check that all your information is valid and try again.');
        }
        // For other database errors, provide a generic but helpful message
        throw new Error('There was a problem creating your family account. Please try again or contact support if the problem persists.');
      }

      console.log('✅ Family account setup completed');
      return { success: true, family };

    } catch (error) {
      console.error('Error completing family account setup:', error);
      throw error;
    }
  }
}

export default supabase;

// Add this debug function to check connection
export const debugSupabase = async () => {
  try {
    const { data: families, error } = await supabase
      .from('families')
      .select('id, family_name')
      .limit(1);
    
    console.log('Supabase connection test:', { families, error });
    return { connected: !error, families, error };
  } catch (error) {
    console.error('Supabase connection failed:', error);
    return { connected: false, error };
  }
}; 